import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface UsePaymentConfirmedProcurementsProps {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
}

/**
 * Optimized hook for confirmed payments page
 * Fetches only procurements with confirmed payments
 * with all related data in a single query to avoid N+1 problems
 */
export const usePaymentConfirmedProcurements = ({ 
  page = 1, 
  pageSize = 10,
  startDate,
  endDate
}: UsePaymentConfirmedProcurementsProps = {}) => {
  return useQuery({
    queryKey: ['payment-confirmed-procurements', page, pageSize, startDate, endDate],
    queryFn: async () => {
      console.log(`Fetching confirmed payment procurements - Page ${page}, Size ${pageSize}...`);
      
      // Fetch procurements that have confirmed payments
      let query = supabase
        .from('procurements')
        .select(`
          id,
          product_name,
          total_quantity,
          total_amount,
          source_type,
          source_id,
          status,
          created_at,
          requestor_id,
          finance:procurement_finance!inner(
            payment_term_type,
            payment_term_days,
            payment_due_date,
            approved_by,
            finance_approved_at,
            payment_confirmed,
            payment_confirmed_by,
            payment_confirmed_at,
            actual_payment_date,
            payment_confirmation_notes,
            has_advance_payment,
            advance_payment_amount,
            remaining_payment_amount,
            advance_payment_confirmed,
            advance_payment_confirmed_by,
            advance_payment_confirmed_at,
            remaining_payment_confirmed,
            remaining_payment_confirmed_by,
            remaining_payment_confirmed_at
          ),
          quality:procurement_quality(
            damage_kg,
            compensation_kg,
            grading_type,
            preliminary_damage_kg,
            preliminary_compensation_kg,
            preliminary_damage_percentage,
            preliminary_graded_at,
            preliminary_notes,
            final_grading_expected_date,
            final_graded_at,
            is_final_grading_complete
          ),
          requestor:user_profiles!fk_procurements_requestor(
            id,
            first_name,
            last_name,
            role
          )
        `, { count: 'exact' })
        .eq('finance.payment_confirmed', true); // Only confirmed payments
      
      // Apply date range filters if provided using the actual table name
      if (startDate) {
        query = query.gte('procurement_finance.payment_confirmed_at', startDate);
      }
      if (endDate) {
        query = query.lte('procurement_finance.payment_confirmed_at', endDate);
      }
      
      const { data: procurements, error, count } = await query
        .order('created_at', { ascending: false })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (error) {
        console.error('Error fetching confirmed payment procurements:', error);
        throw error;
      }

      if (!procurements) {
        return [];
      }

      console.log(`Fetched ${procurements.length} confirmed payment procurements`);

      // Fetch source names in parallel for all unique source combinations
      const sourceQueries = new Map<string, Set<string>>();
      procurements.forEach(p => {
        if (p.source_type && p.source_id) {
          if (!sourceQueries.has(p.source_type)) {
            sourceQueries.set(p.source_type, new Set());
          }
          sourceQueries.get(p.source_type)!.add(p.source_id);
        }
      });

      // Fetch all source names in parallel
      const sourceNamePromises = Array.from(sourceQueries.entries()).map(async ([sourceType, sourceIds]) => {
        const ids = Array.from(sourceIds);
        
        switch (sourceType) {
          case 'supplier':
            const { data: suppliers } = await supabase
              .from('suppliers')
              .select('id, name')
              .in('id', ids);
            return { type: 'supplier', data: suppliers || [] };
            
          case 'agent':
            const { data: agents } = await supabase
              .from('agents')
              .select('id, name')
              .in('id', ids);
            return { type: 'agent', data: agents || [] };
            
          case 'farm':
            const { data: farms } = await supabase
              .from('farms')
              .select('id, farmer_name as name')
              .in('id', ids);
            return { type: 'farm', data: farms || [] };
            
          default:
            return { type: sourceType, data: [] };
        }
      });

      const sourceResults = await Promise.all(sourceNamePromises);
      
      // Create source name lookup map
      const sourceNameMap = new Map<string, string>();
      sourceResults.forEach(result => {
        result.data.forEach((item: any) => {
          sourceNameMap.set(`${result.type}-${item.id}`, item.name);
        });
      });

      // Enrich procurements with source names
      const enrichedProcurements = procurements.map(procurement => ({
        ...procurement,
        source_name: procurement.source_type && procurement.source_id
          ? sourceNameMap.get(`${procurement.source_type}-${procurement.source_id}`) || 'Unknown'
          : 'N/A'
      }));

      console.log('Successfully enriched confirmed payment procurements with source names');
      return {
        data: enrichedProcurements as any[],
        count: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        currentPage: page,
        pageSize
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes cache
    gcTime: 1000 * 60 * 5, // 5 minutes garbage collection
  });
};